import { useReactFlow } from '@xyflow/react';
import { useCallback } from 'react';

import { useEditCanvasStore } from '../../../store';
import { moveBack, moveForward, type NodeZIndex, sendToBack, sendToFront } from '../../../utils/nodeLayering';
import { commands } from '../commands';
import { useCommandHistory } from './useCommandHistory';

export function useLayerCommands() {
  const execute = useCommandHistory((state) => state.execute);
  const { getNodes } = useReactFlow();

  const executeLayerOperation = useCallback(
    (operationFn: (allNodes: NodeZIndex[], selectedIds: Set<string>) => NodeZIndex[]) => {
      const selectedIds = new Set(
        getNodes()
          .filter((n) => n.selected)
          .map((n) => n.id),
      );

      if (selectedIds.size === 0) {
        return;
      }

      const currentZIndexes: NodeZIndex[] = useEditCanvasStore
        .getState()
        .nodes.filter((n) => n.type !== 'expandingBoundingBox')
        .map((n) => ({ id: n.id, zIndex: n.zIndex ?? 0 }));

      const changes = operationFn(currentZIndexes, selectedIds);
      if (changes.length === 0) {
        return;
      }

      execute(commands.updateZIndexes(currentZIndexes, changes));
    },
    [execute, getNodes],
  );

  const onMoveForward = useCallback(() => executeLayerOperation(moveForward), [executeLayerOperation]);
  const onMoveBack = useCallback(() => executeLayerOperation(moveBack), [executeLayerOperation]);
  const onSendToFront = useCallback(() => executeLayerOperation(sendToFront), [executeLayerOperation]);
  const onSendToBack = useCallback(() => executeLayerOperation(sendToBack), [executeLayerOperation]);

  return { onMoveBack, onMoveForward, onSendToBack, onSendToFront };
}
